import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../../../../context/UseTemplateEditorContext';
import { getTemplateVersionHistory } from '../../../../utils/queries';
import {
  Button,
  Gap,
  Heading,
  P,
  Page,
  VersionHistoryTable,
  IVersion,
  VersionHistoryTableLabels,
} from '@salecycle/ui-component-library';
import { LeftConfigContainer } from '../../../style';
import { ITemplateHistoryItem } from 'entities/template/template';
import { TemplateEditorSaveUnsavedChangesModal } from '../../../modals/TemplateEditorSaveUnsavedChangesModal';
import useNotifications from 'core/notifications/useNotifications';

export const TemplateHistoryView = () => {
  const { t } = useTranslation(['designs']);
  const { siteId, id: templateId, language } = useParams<{ siteId: string; id: string; language: string }>();
  const { editorSettings, hasUnsavedChanges, templateHistory, previewingVersion } = useTemplateEditorState();

  const dispatch = useTemplateEditorDispatch();
  const { showNotification } = useNotifications();

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const { isLoading, isSuccess, data, isIdle, refetch } = useQuery(
    ['templateHistory'],
    () => getTemplateVersionHistory({ siteId, templateId, language, t }),
    {
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  React.useEffect(() => {
    if (isIdle || isLoading) {
      return;
    }

    if (!isSuccess) {
      showNotification(t('designs:designGallery.templateEditor.tabBar.notifications.templateHistoryError'), 'error');
      return;
    }

    if (data && data.length > 0) {
      dispatch({
        type: 'UPDATE_TEMPLATE_HISTORY',
        payload: { history: data },
      });
    } else {
      showNotification(t('designs:designGallery.templateEditor.tabBar.notifications.noTemplateHistory'), 'error');
    }
  }, [isLoading, isSuccess, isIdle, dispatch, data, showNotification, t]);

  const previewVersion = (versionId: string) => {
    showNotification(`Preview template version: ${versionId}`, 'normal');
    const setVersion = data && data.length > 0 && data[0].versionId === versionId ? '' : versionId;
    dispatch({
      type: 'SET_TEMPLATE_VERSION',
      payload: { versionId: setVersion },
    });

    dispatch({
      type: 'SET_EDITOR_SETTINGS',
      payload: {
        ...editorSettings,
        readOnly: setVersion.length > 0,
      },
    });
  };

  const handlePreview = (version: IVersion) => {
    if (hasUnsavedChanges) {
      setSaveDialogOpen(true);
      return;
    }
    previewVersion(version.id);
  };

  const versions: IVersion[] = useMemo(
    () =>
      (templateHistory ?? []).map((item: ITemplateHistoryItem) => ({
        id: item.versionId,
        date: item.date,
        authorName: item.name,
        comment: item.comment,
        authorEmail: item.email,
        authorId: item.userId,
      })),
    [templateHistory],
  );

  const labels: VersionHistoryTableLabels = useMemo(
    () => ({
      lastUpdated: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.lastUpdated'),
      updatedBy: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.updatedBy'),
      comment: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.comment'),
      preview: t('designs:designGallery.templateEditor.versionHistory.table.columnHeader.preview'),
      currentVersionBadge: t('designs:designGallery.templateEditor.versionHistory.table.label.currentVersionBadge'),
      selected: t('designs:designGallery.templateEditor.versionHistory.table.label.statusSelected'),
    }),
    [t],
  );

  const currentVersionId = data?.[0]?.versionId;

  return (
    <>
      <Page>
        <LeftConfigContainer data-testid="TemplateHistoryView">
          <Heading variation="h2">{t('designs:designGallery.templateEditor.versionHistory.title')}</Heading>
          <Gap top={20} />
          {templateHistory !== undefined && templateHistory.length > 0 && (
            <VersionHistoryTable
              versions={versions}
              selectedVersionId={previewingVersion}
              currentVersionId={currentVersionId}
              onPreview={handlePreview}
              labels={labels}
            />
          )}
          {(templateHistory === undefined || templateHistory.length < 1) && isLoading && (
            <P data-testid="loadingIndicator">
              {t('designs:designGallery.templateEditor.versionHistory.table.label.statusLoading')}
            </P>
          )}
          {(templateHistory === undefined || templateHistory.length < 1) && !isLoading && (
            <>
              <P data-testid="notAvailableIndicator">
                {t('designs:designGallery.templateEditor.versionHistory.table.label.statusNotAvailable')}
              </P>
              <P>
                <Button variation="secondary" onClick={() => refetch()}>
                  {t('designs:designGallery.templateEditor.versionHistory.table.label.buttonReload')}
                </Button>
              </P>
            </>
          )}
        </LeftConfigContainer>
      </Page>
      <TemplateEditorSaveUnsavedChangesModal
        modalOpen={saveDialogOpen}
        setModalOpen={setSaveDialogOpen}
        onSave={() => {
          showNotification(t('designs:designGallery.templateEditor.modals.unsavedChanges.saveSuccess'), 'normal');
          // Do not go on with preview, but let the user select version again
          refetch();
        }}
        onDiscard={() => {
          showNotification(t('designs:designGallery.templateEditor.modals.unsavedChanges.discardSuccess'), 'normal');
          // Do not go on with preview, but let the user select version again
          refetch();
        }}
      />
    </>
  );
};
